import React, { useState } from 'react';
import { ChevronRight, Info, ClipboardList } from 'lucide-react';
import './App.css';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';  // <-- Import Framer Motion
import Header from './components/Header';
import AppleVideoExperience from './components/VideoSection';
import Footer from './components/Footer';
import Products from './components/Products';  // Import the Products component
import ContactUs from './components/ContactUs';
import AboutUs from './components/AboutUs';
import PricingPage from './components/PricingPage';
import Signup from './components/Signup';
import Sign from './components/Sign';
import OtpVerification from './components/OtpVerification';
import Account from './components/account';
import Success from './components/Success';
import Cancel from './components/Cancel';
import PrivacyPolicy from './components/PrivacyPolicy';
import DownloadPage from './components/DownloadPage';
import LogoSection from './components/LogoSection';
import CookiePopup from './components/CookiePopup';
import Trustmark from './components/trustmark';
import TrustmarkPricing from './components/TrustmarkPricing';
import WebsiteForm from './components/WebsiteForm';
import Newsletter from './components/Newsletter';
import HRConsulting from './components/hrconsulting';
import CyberSecurity from './components/cybersecurity';
import PartnerPortal from './components/Partnerportal';
import MetricsSandbox from './components/Metricssandbox';
import Carousel from './components/Carousal';
import BoxBreathing from './components/BoxBreathing';
import Certification from './components/Certification';
import BadgeCTA from './components/certificationSection';
import SafePulse from './components/SafePulse';
import WhatWeOffer from './components/WhatWeOffer';
import WhoIsItFor from './components/WhoIsItFor';
import SafePulseHomeSection from './components/SafePulseHomeSection';
import SafePulseFeaturesSection from './components/SafePulseFeaturesSection';
import SafePulseInteractive from './components/SafePulseInteractive';
import WellnessRiskAssessment from './components/WellnessRiskAssessment';
import CybersecurityRiskAssessment from './components/CybersecurityRiskAssessment';
import Blogs from './components/Blogs';
import AdminBlogs from './components/AdminBlogs';
import AdminBlogLogin from './components/AdminBlogLogin';
import BlogDetail from './components/BlogDetail';
import AccountDeletion from './components/AccountDeletion';
import TrainingPortal from './components/TrainingPortal';
import Whitepapers from './components/Whitepapers';
import SafePulseBrowser from './components/SafePulseBrowser';
import ClearFrame from './components/ClearFrame';
import ClearSense from './components/ClearSense';
import ClearSenseHero from './components/ClearSenseHero';
import ClearSeal from './components/ClearSeal';

function Home() {
  const navigate = useNavigate();
  const [csInput, setCsInput] = useState('');

  const handleCsCheck = () => {
    const trimmed = csInput.trim();
    if (trimmed) {
      localStorage.setItem('clearsense_hero_input', trimmed);
    }
    navigate('/clearsense');
  };

  const stats = [
    { number: '6+', label: 'AI Products' },
    { number: 'Free', label: 'To Get Started' },
    { number: '£19.99', label: 'Pro / Month' },
    { number: '100%', label: 'Data Secure' },
  ];

  return (
    <main className="apple-main">
      <Helmet>
        <title>Erasys - AI-Powered Business Solutions</title>
        <meta
          name="description"
          content="Erasys delivers AI-powered tools for answer verification, cybersecurity, workforce wellbeing, and data intelligence — built for the modern enterprise."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="apple-hero">
        <div className="apple-container">
          {/* Main Content */}
          <motion.div
            className="apple-hero-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <div className="apple-hero-text">
              <motion.span
                className="apple-hero-eyebrow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Erasys Intelligence Platform
              </motion.span>

              <motion.h1
                className="apple-hero-title"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Intelligence Built for the
                <br />
                <span className="apple-hero-accent">Modern Enterprise.</span>
              </motion.h1>

              <motion.p
                className="apple-hero-subtitle"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                Six tools. One ecosystem. Infinite possibilities.
              </motion.p>

              <motion.p
                className="apple-hero-description"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                From <strong>AI answer verification</strong> to <strong>cybersecurity</strong>, workforce wellbeing, and data quality — Erasys brings enterprise-grade intelligence across every dimension of your organisation.
              </motion.p>

              <motion.div
                className="apple-hero-buttons"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9 }}
              >
                <motion.button
                  className="apple-btn apple-btn-primary"
                  onClick={() => navigate('/clearsense')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ClipboardList className="apple-btn-icon" />
                  Try ClearSense Free
                  <ChevronRight className="apple-btn-arrow" />
                </motion.button>
                <motion.button
                  className="apple-btn apple-btn-secondary"
                  onClick={() => document.querySelector('.offer-section')?.scrollIntoView({ behavior: 'smooth' })}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Info className="apple-btn-icon" />
                  Explore Products
                </motion.button>
              </motion.div>
            </div>

            {/* Hero ClearSense Input */}
            <motion.div
              className="apple-hero-image apple-hero-clearsense"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              <div className="cs-hero-input-panel">
                <textarea
                  className="cs-hero-textarea"
                  value={csInput}
                  onChange={(e) => setCsInput(e.target.value)}
                  placeholder="Paste ChatGPT, Copilot, Claude, or any AI-generated text here…"
                  spellCheck={false}
                />
                <button className="cs-hero-check-btn" onClick={handleCsCheck}>
                  <ClipboardList size={14} />
                  Check
                </button>
              </div>
            </motion.div>
          </motion.div>

          {/* Stats Strip */}
          <motion.div
            className="apple-stats-strip"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            {stats.map((stat, i) => (
              <React.Fragment key={i}>
                <div className="apple-stat-item">
                  <span className="apple-stat-number">{stat.number}</span>
                  <span className="apple-stat-label">{stat.label}</span>
                </div>
                {i < stats.length - 1 && <div className="apple-stat-divider" />}
              </React.Fragment>
            ))}
          </motion.div>
        </div>

        {/* Floating Elements */}
        <div className="apple-floating-elements">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="apple-floating-dot"
              style={{
                left: `${15 + i * 12}%`,
                top: `${20 + (i % 3) * 25}%`,
              }}
              animate={{
                y: [-10, 10, -10],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 3 + i * 0.3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>
      </section>
    </main>
  );
}

// Move Header/Footer conditional logic into Layout component
function Layout({ children }) {
  const location = useLocation();  // useLocation inside Router context

  // Paths where you don't want the Header and Footer
  const hideHeaderFooterRoutes = ['/signup', '/signin','/otp'];

  return (
    <>
      {/* Conditionally render Header */}
      {!hideHeaderFooterRoutes.includes(location.pathname) && <Header />}
      
      {/* Render the main content */}
      {children}

      {/* Conditionally render Footer */}
      {!hideHeaderFooterRoutes.includes(location.pathname) && <Footer />}
    </>
  );
}


function App() {
  return (
    <Router>
      <div className="App">
        <Layout>
          <CookiePopup/>
          <Routes>
            <Route 
              path="/" 
              element={
                <>
                  <Home />  
                  <ClearSenseHero />
                  
                  <WhatWeOffer />
                  <WhoIsItFor />
                  <SafePulseHomeSection />
                  <SafePulseFeaturesSection />
                  <SafePulseInteractive />
                  
                  <BoxBreathing />

                  <AppleVideoExperience />
                  
                  <BadgeCTA/>
                   <Carousel/>
                  
                 <LogoSection />
                  <Newsletter/>
                  
                </>
              } 
            />
            
            {/* Route for the Products page */}
            <Route path="/products" element={<Products />} />
            <Route path="/safepulse" element={<SafePulse />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signin" element={<Sign />} />
            <Route path="/otp" element={<OtpVerification/>} />
            <Route path="/account" element={<Account/>} />
            <Route path="/success" element={<Success/>} />
            <Route path="/websiteform" element={<WebsiteForm/>} />
            <Route path="/cancel" element={<Cancel/>} />
            <Route path="/privacy" element={<PrivacyPolicy/>} />
            <Route path="/download" element={<DownloadPage/>} />
            <Route path="/TrustMark" element={<Trustmark/>} />
            <Route path="/Hrconsulting" element={<HRConsulting/>} />
            <Route path="/TrustMark-pricing" element={<TrustmarkPricing/>} />
            <Route path="/cybersecurity" element={<CyberSecurity/>} />
            <Route path="/wellness-risk-assessment" element={<WellnessRiskAssessment/>} />
            <Route path="/cybersecurity-risk-assessment" element={<CybersecurityRiskAssessment/>} />
            <Route path="/partnerportal" element={<PartnerPortal/>} />
            <Route path="/Metrics" element={<MetricsSandbox/>} />
            <Route path="/apple" element={<Carousel/>} />
            <Route path="/certification" element={<Certification/>} />
            <Route path="/blogs" element={<Blogs/>} />
            <Route path="/blogs/:id" element={<BlogDetail/>} />
            <Route path="/admin/blog-login" element={<AdminBlogLogin/>} />
            <Route path="/admin/blogs" element={<AdminBlogs/>} />
            <Route path="/safepulse/account-deletion" element={<AccountDeletion/>} />
            <Route path="/safepulse-browser" element={<SafePulseBrowser/>} />
            <Route path="/clearframe" element={<ClearFrame/>} />
            <Route path="/clearsense" element={<ClearSense/>} />
            <Route path="/clearseal" element={<ClearSeal/>} />
            <Route path="/training" element={<TrainingPortal/>} />
            <Route path="/whitepapers" element={<Whitepapers/>} />
          </Routes>
        </Layout>
      </div>
    </Router>
  );
}

export default App;
